import type { LegalEntityBindingInput, LegalEntityLinked } from '@/services/bff/legalEntity.api';
/**
 * 基金生命周期阶段（与后端 InvestmentFundPhase 一致）。
 * 「筹备中」已退役：仅保留在类型里兼容未迁移的存量行，不再出现在可选列表。
 */
export type InvestmentFundPhase =
  | '筹备中'
  | '募集期'
  | '投资期'
  | '退出期'
  | '延长期'
  | '清算中'
  | '清算完成'
  | '已退出'
  | '已注销';

/** 基金管理类型：自管=我方作为 GP 管理；参股=我方作为 LP 出资。 */
export type InvestmentFundControlType = '自管' | '参股';
export type FundManagementMode = 'self_managed' | 'external_managed';
export type FundManagementBasis =
  | 'internal_manager'
  | 'majority_commitment'
  | 'both'
  | 'external_manager';
export type FundClassificationStatus = 'confirmed' | 'pending_review';

export const FUND_MANAGEMENT_MODE_LABELS: Record<FundManagementMode, string> = {
  self_managed: '我方管理',
  external_managed: '外部管理 / 参股',
};

/** 基金注册状态：已注册才维护工商登记信息；未注册不填。 */
export type FundRegistrationStatus = '已注册' | '未注册';
export const FUND_REGISTRATION_STATUS_OPTIONS: {
  value: FundRegistrationStatus;
  label: string;
}[] = [
  { value: '未注册', label: '未注册' },
  { value: '已注册', label: '已注册' },
];

/** 基金类型按投资阶段或策略分类。 */
export type FundType =
  | '天使基金'
  | '创业投资基金'
  | '股权投资基金'
  | '成长基金'
  | '产业基金'
  | '政府引导基金'
  | '母基金'
  | '并购基金';
export const FUND_TYPE_OPTIONS: { value: FundType; label: FundType }[] = [
  { value: '天使基金', label: '天使基金' },
  { value: '创业投资基金', label: '创业投资基金' },
  { value: '股权投资基金', label: '股权投资基金' },
  { value: '成长基金', label: '成长基金' },
  { value: '产业基金', label: '产业基金' },
  { value: '政府引导基金', label: '政府引导基金' },
  { value: '母基金', label: '母基金' },
  { value: '并购基金', label: '并购基金' },
];
export const FUND_TYPE_VALUES = FUND_TYPE_OPTIONS.map((item) => item.value);
/**
 * 基金类型口径：`inv_fund.fund_type` 是唯一可写字段；`inv_fund.type`（原「投资类别」）
 * 已降级为只读历史列，服务端永不写入，只对极旧档案（母基金/子基金/历史投资类别值）有值。
 * 因此一律 fundType 优先、type 兜底，且必须走下面两个解析器，不得自己写 `type || '—'`：
 * 破折号是 truthy，一旦在 domain 层把空值折叠成占位符，所有 `||` 兜底与筛选都会静默失效。
 */
/** 真实值口径（筛选项、筛选条件、导出）：无有效类型时返回空串，绝不返回占位符。 */
export const resolveFundTypeValue = (
  fundType?: string | null,
  legacyType?: string | null,
): string => (fundType ?? '').trim() || (legacyType ?? '').trim();
/** 展示口径：两者都为空时给占位符「—」。 */
export const resolveFundTypeLabel = (
  fundType?: string | null,
  legacyType?: string | null,
): string => resolveFundTypeValue(fundType, legacyType) || '—';

/** 基金级别：一级 / 二级 / 三级。 */
export type FundLevel = '一级' | '二级' | '三级' | '四级';

/** 是否简易管理：是 / 否。 */
export type FundSimplifiedManagement = '是' | '否';
export const FUND_LEVEL_OPTIONS: { value: FundLevel; label: string }[] = [
  { value: '一级', label: '一级' },
  { value: '二级', label: '二级' },
  { value: '三级', label: '三级' },
  { value: '四级', label: '四级' },
];

/** 是否类字段口径（存续情况 / 托管协议）。 */
export type FundYesNo = '是' | '否';
export const FUND_YES_NO_OPTIONS: { value: FundYesNo; label: string }[] = [
  { value: '是', label: '是' },
  { value: '否', label: '否' },
];

/** 投资期管理费提取方式。 */
export type FundInvestmentMgmtFeeBasis = '按认缴' | '按实缴' | '其他';
export const FUND_INVESTMENT_MGMT_FEE_BASIS_OPTIONS: {
  value: FundInvestmentMgmtFeeBasis;
  label: string;
}[] = [
  { value: '按认缴', label: '按认缴' },
  { value: '按实缴', label: '按实缴' },
  { value: '其他', label: '其他' },
];

/** 退出期管理费提取方式。 */
export type FundExitMgmtFeeBasis = '按实际未退出金额' | '其他';
export const FUND_EXIT_MGMT_FEE_BASIS_OPTIONS: {
  value: FundExitMgmtFeeBasis;
  label: string;
}[] = [
  { value: '按实际未退出金额', label: '按实际未退出金额' },
  { value: '其他', label: '其他' },
];

/** 延长期管理费。 */
export type FundExtensionMgmtFeeType = '不收取' | '其他';
export const FUND_EXTENSION_MGMT_FEE_TYPE_OPTIONS: {
  value: FundExtensionMgmtFeeType;
  label: string;
}[] = [
  { value: '不收取', label: '不收取' },
  { value: '其他', label: '其他' },
];
export const FUND_MANAGEMENT_BASIS_LABELS: Record<FundManagementBasis, string> =
  {
    internal_manager: '我方担任管理人',
    majority_commitment: '我方认缴超过 50%',
    both: '我方管理人 + 认缴超过 50%',
    external_manager: '外部管理人',
  };

/** 基金性质枚举（与后端 FundKind 枚举一致）*/
export enum FundKind {
  BLIND_POOL = 'blind_pool',
  SPECIFIC = 'specific',
  CVC = 'cvc',
  FUND_OF_FUNDS = 'fund_of_funds',
  INDUSTRIAL = 'industrial',
}

/** 基金性质显示名称 */
export const FUND_KIND_LABELS: Record<FundKind, string> = {
  [FundKind.BLIND_POOL]: '盲池基金',
  [FundKind.SPECIFIC]: '专项基金',
  [FundKind.CVC]: 'CVC直投',
  [FundKind.FUND_OF_FUNDS]: '母基金',
  [FundKind.INDUSTRIAL]: '产业基金',
};

/** 基金性质说明 */
export const FUND_KIND_DESCRIPTIONS: Record<FundKind, string> = {
  [FundKind.BLIND_POOL]: '先募集资金，再寻找投资项目，适用于市场化PE/VC基金',
  [FundKind.SPECIFIC]: '针对特定项目设立，先锁定项目再募集资金',
  [FundKind.CVC]: '企业用自有资金直接投资，不对外募资，快速决策',
  [FundKind.FUND_OF_FUNDS]: '投资其他基金，不直接投资企业，分散风险',
  [FundKind.INDUSTRIAL]: '围绕特定产业链投资，产业协同',
};

/** 基金类型配置 */
export interface FundKindConfig {
  requiresPartnership: boolean; // 是否需要基金载体
  requiresFundraising: boolean; // 是否需要募资
  icon: string; // 图标
  color: string; // 主题色
}

export const FUND_KIND_CONFIGS: Record<FundKind, FundKindConfig> = {
  [FundKind.BLIND_POOL]: {
    requiresPartnership: true,
    requiresFundraising: true,
    icon: '💼',
    color: '#1890ff',
  },
  [FundKind.CVC]: {
    requiresPartnership: false,
    requiresFundraising: false,
    icon: '🏢',
    color: '#52c41a',
  },
  [FundKind.SPECIFIC]: {
    requiresPartnership: true,
    requiresFundraising: true,
    icon: '🎯',
    color: '#faad14',
  },
  [FundKind.FUND_OF_FUNDS]: {
    requiresPartnership: true,
    requiresFundraising: true,
    icon: '📊',
    color: '#722ed1',
  },
  [FundKind.INDUSTRIAL]: {
    requiresPartnership: true,
    requiresFundraising: true,
    icon: '🏭',
    color: '#13c2c2',
  },
};

/** 基金募集状态（正交维度）。 */
export type FundraisingStatus =
  | '未开始'
  | '募集进行中'
  | '首关完成'
  | '终关完成'
  | '募集完成';

/** 基金产品备案状态（正交维度）。 */
export type FundFilingStatus =
  | '未准备'
  | '待提交'
  | '办理中'
  | '待补充材料'
  | '已备案';

/** 基金生命周期阶段显示标签（纯GP体系下，自管与参股基金均统一遵循基金自身生命周期）。 */
export const getFundPhaseLabel = (
  phase?: string | null,
  _controlType?: InvestmentFundControlType,
) => phase || '—';

/** 后端 inv_fund 实体返回结构（InvestmentFund）。 */
export interface FundLegalDetails {
  basicProfile?: {
    investmentDirection?: string;
    investmentExtensionYears?: number;
    extensionPeriodYears?: number;
    filingStatus?: '已备案' | '未备案';
  };
  custodyMode?: 'custodied' | 'not_custodied';
  custodyExemption?: 'holder_safekeeping_body' | 'contractual_safeguards';
  safekeepingMeasures?: string;
  disputeResolution?: string;
  custodyEvidenceUuid?: string;
  investsViaSpv?: boolean;
  allowsAdditionalSubscription?: boolean;
  executivePartnerUuid?: string;
  managementArrangement?: 'self_managed' | 'delegated';
  managementAgreementUuid?: string;
  shareRegistrationMode?: 'manager' | 'delegated';
  shareRegistrarUuid?: string;
  shareRegistrationDescription?: string;
  performanceFeeRecipientKind?: 'manager' | 'investor';
  performanceFeeRecipientUuid?: string;
}

export interface InvestmentFundRecord extends LegalEntityLinked {
  legalFormDetails?: FundLegalDetails | null;
  hasActiveTerms?: boolean;
  basisRevision?: number;
  phaseBasis?: { source: string; firstCloseAt: string | null; investmentEndAt: string | null; exitEndAt: string | null; extensionEndAt: string | null; warnings: string[] };
  id: number;
  uuid: string;
  tenantUuid: string;
  generalPartnerUuid?: string | null;
  /** 基金内部编码（业务/财务代号）。 */
  code?: string | null;
  name: string;
  shortName: string | null;
  type: string | null;
  registrationType?: string | null;
  organizationForm?: string | null;
  manager: string | null;
  /** 基金经理 IAM 成员身份。 */
  managerMemberUuid?: string | null;
  /** 风控经理显示名（与基金经理并列）。 */
  riskManager?: string | null;
  /** 风控经理 IAM 成员身份，用于基金内审核人匹配。 */
  riskManagerMemberUuid?: string | null;
  registrationNumber?: string | null;
  registrationDate?: string | null;
  /** 分配瀑布（4 层结构，由「条款 / 延期」入口维护）。 */
  distributionWaterfall?: {
    enabledLayers: number;
    layerRatios: [number, number, number, number];
  } | null;
  /** 分配频率：季度 / 半年 / 年度。 */
  distributionFrequency?: string | null;

  // ── 基本信息补齐（2026-10-23 契约）：存续情况 / 管理费三段 / 收益分配 / 其他要素 / 返投(参股) ──
  /** 要求返投比例（%）；参股基金使用。 */
  requestReturnRatio?: number | string | null;
  /** 要求返投金额（万元）；参股基金使用。 */
  requestReturnAmount?: number | string | null;
  returnRequirement?: string | null;
  returnCompletion?: string | null;
  /** 投资期（年）。 */
  investmentPeriodYears?: number | null;
  /** 退出期（年）。 */
  exitPeriodYears?: number | null;
  /** 是否延长：是 / 否。 */
  isRollOver?: FundYesNo | string | null;
  /** 是否退出期已延长：是 / 否。 */
  isPaybackOver?: FundYesNo | string | null;
  investmentPeriodStartAt?: string | null;
  investmentPeriodExtendEndAt?: string | null;
  durationDesc?: string | null;
  investmentManagementFeeBasis?: FundInvestmentMgmtFeeBasis | string | null;
  exitManagementFeeBasis?: FundExitMgmtFeeBasis | string | null;
  /** 退出期管理费率（%）。 */
  exitManagementFeeRate?: number | string | null;
  extensionManagementFeeType?: FundExtensionMgmtFeeType | string | null;
  managementFeeDesc?: string | null;
  incomeDistributionDesc?: string | null;
  /** 超额分配给管理人（%）。 */
  managerExcessRatio?: number | string | null;
  /** 超额分配给有限合伙人（%）。 */
  lpExcessRatio?: number | string | null;
  custodyFeeDesc?: string | null;
  executionServiceFee?: string | null;
  businessScope?: string | null;
  /** 是否有托管协议：是 / 否。 */
  custodyAgreementRequired?: FundYesNo | string | null;
  delegatedRepresentative?: string | null;
  /** 所属公司组织节点 id（组织树根节点）。 */
  companyId?: string | null;
  /** 所属公司（我方内部组织，非基金管理人）。 */
  companyName?: string | null;
  /** 所属部门组织节点 id（公司下级）。 */
  deptId?: string | null;
  /** 所属部门。 */
  departmentName?: string | null;
  /** 所属小组组织节点 id（部门下级，可空）。 */
  orgGroupId?: string | null;
  /** 所属小组。 */
  groupName?: string | null;
  /** 分配计算基准：成本 / 公允。 */
  distributionBasis?: string | null;
  /** 封闭期（月）。 */
  lockupMonths?: number | null;
  /** 锁定期（月）。 */
  holdingLockupMonths?: number | null;
  managementEntity?: string | null;
  partnershipEntity?: string | null;
  partnershipOrganizationUuid?: string | null;
  extensionEndAt?: string | null;
  extensionApprovalAttachmentUuid?: string | null;
  managementFeeRate?: string | number | null;
  carryRate?: string | number | null;
  hurdleRate?: string | number | null;
  /** GP 收益（carry/catch-up）受信 LP 组织 UUID。 */
  gpCarryLpOrgUuid?: string | null;
  ownerName: string | null;
  ownerUuid: string | null;
  /** 金额单位：万元（decimal 可能以字符串返回）。 */
  targetAmount: number | string | null;
  minimumAmount: number | string | null;
  firstCloseTarget: number | string | null;
  raisingStarted?: boolean;
  raisingStartedAt?: string | null;
  firstCloseAt: string | null;
  finalCloseAt: string | null;
  /** 我方认缴规模（万元）：自管=我方作为 GP 的认缴，参股=我方作为 LP 的认缴。 */
  gpCommitmentAmount?: number | string | null;
  /** @deprecated 兼容历史数据，不再区分我方 LP 认缴。 */
  ourLpCommitmentAmount?: number | string | null;
  /** 终关目标规模（万元）。 */
  finalCloseTarget?: number | string | null;
  establishedAt: string | null;
  durationYears: number | null;
  region: string | null;
  regionUuid?: string | null;
  strategy: string | null;
  industries: string[] | null;
  currency: string;
  phase: InvestmentFundPhase;
  controlType?: InvestmentFundControlType;
  ourShareRatio?: number | string | null;
  investmentDecisionDate?: string | null;
  investmentDecisionNo?: string | null;
  decisionConclusion?: string | null;
  lpaSignDate?: string | null;
  gpContactName?: string | null;
  advisorySeat?: string | null;
  keyPersonClause?: string | null;
  managementMode?: FundManagementMode | null;
  managementBasis?: FundManagementBasis | null;
  classificationStatus?: FundClassificationStatus;
  internalManagerOrgUuid?: string | null;
  managementClassificationNote?: string | null;
  totalCommitmentAmount?: number | string | null;
  totalCommitmentSource?: string | null;
  totalCommitmentAsOf?: string | null;
  totalCommitmentAttachmentUuid?: string | null;
  ourInvestorOrgUuids?: string[];
  isAbsoluteControl?: boolean;
  controlRemark?: string | null;
  /** 出资主体UUID（B 模型必填；platform 或 fund）。 */
  investmentSubjectUuid?: string | null;
  /** 出资主体类型：platform=管理平台出钱，fund=基金出钱（参股档案）。 */
  investmentSubjectKind?: 'platform' | 'fund' | null;
  /** 出资主体名称快照（服务端回填）。 */
  investmentSubjectName?: string | null;
  /** 来源主体类型快照；历史响应优先使用 sourceSubjectKind，旧字段仅兼容。 */
  sourceSubjectKind?: string | null;
  /** 来源主体 UUID 快照；不得从投资人台账的 lpOrgUuid 推断。 */
  sourceSubjectUuid?: string | null;
  /** 来源主体名称快照。 */
  subjectName?: string | null;
  /** 管理平台 UUID（独立字段，可空）。 */
  managementPlatformUuid?: string | null;
  /** 管理平台名称快照（服务端回填）。 */
  managementPlatformName?: string | null;
  /** 历史归属平台字段（兼容旧响应）。 */
  ownerPlatformUuid?: string | null;
  ownerPlatformName?: string | null;
  /** 来源基金 UUID（参股档案可选）。 */
  sourceFundUuid?: string | null;
  /** 基金性质（枚举值）：blind_pool | specific | cvc | fund_of_funds | industrial */
  fundKind?: FundKind | string | null;
  /** 基金注册状态：已注册 / 未注册（未注册时不维护工商登记信息）。 */
  registrationStatus?: FundRegistrationStatus | string | null;
  /** 基金类型：天使/创投/股权/成长/产业/政府引导/母基金/并购。 */
  fundType?: FundType | string | null;
  /** 基金级别：一级 / 二级 / 三级。 */
  fundLevel?: FundLevel | string | null;
  /** 法定代表人或执行事务合伙人。 */
  legalRepresentative?: string | null;
  /** 基金自身统一社会信用代码（区别于基金管理人的统一社会信用代码）。 */
  unifiedSocialCreditCode?: string | null;
  /** 工商登记时间（YYYY-MM-DD）。 */
  businessRegistrationDate?: string | null;
  /** 工商到期日期（YYYY-MM-DD）。 */
  businessRegistrationExpiryDate?: string | null;
  /** 组织机构代码。 */
  organizationCode?: string | null;
  /** 基金注册地址。 */
  registeredAddress?: string | null;
  /** 是否简易管理：是 / 否。 */
  isSimplifiedManagement?: FundSimplifiedManagement | string | null;
  /** 投资边界与组合约束。 */
  strategyType?: string | null;
  investmentRestrictions?: string | null;
  investmentStages?: string[] | null;
  investmentRegions?: string[] | null;
  investmentRegionUuids?: string[] | null;
  minInvestmentAmount?: number | string | null;
  maxInvestmentAmount?: number | string | null;
  targetHoldingRatio?: number | string | null;
  maxPortfolioCount?: number | null;
  allowCoInvestment?: boolean | null;
  allowRelatedParty?: boolean | null;
  fundraisingStartAt?: string | null;
  fundraisingEndAt?: string | null;
  investmentPeriodEndAt?: string | null;
  exitPeriodEndAt?: string | null;
  /** 分配及合规扩展字段。 */
  distributionMode?: string | null;
  complianceNote?: string | null;
  /** 托管人机构 UUID。 */
  custodianUuid?: string | null;
  /** 基金管理人机构 UUID（与 GP 可能不同，指持牌主体）。 */
  fundManagerUuid?: string | null;
  managerUnifiedSocialCreditCode?: string | null;
  /** 募集状态（正交维度）。 */
  fundraisingStatus?: FundraisingStatus;
  /** 备案状态（正交维度）。 */
  filingStatus?: FundFilingStatus;
  /** 国资备案要求（手动维护）。 */
  stateAssetFilingRequired?: boolean | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

/** 基金创建/更新表单值（与后端 DTO 字段对齐）。 */
export interface FundFormValues extends LegalEntityBindingInput {
  /** 完整法律安排快照；未传保留，null 清空。 */
  legalFormDetails?: FundLegalDetails | null;
  expectedRevision?: number;
  name: string;
  code?: string | null;
  shortName?: string | null;
  type?: string | null;
  phase?: InvestmentFundPhase;
  registrationType?: string | null;
  organizationForm?: string | null;
  managementEntity?: string | null;
  partnershipEntity?: string | null;
  partnershipOrganizationUuid?: string | null;
  registrationNumber?: string | null;
  /** 备案日期（YYYY-MM-DD）。 */
  registrationDate?: string | null;
  generalPartnerUuid?: string | null;
  custodianUuid?: string | null;
  fundManagerUuid?: string | null;
  managerUnifiedSocialCreditCode?: string | null;
  finalCloseTarget?: number;
  /** 我方认缴规模（万元）：自管=GP 认缴，参股=LP 认缴。 */
  gpCommitmentAmount?: number;
  /** @deprecated 兼容历史出资口径，统一使用 gpCommitmentAmount。 */
  ourLpCommitmentAmount?: number;
  /** @deprecated 角色由基金类别推导（自管=GP、参股=LP），不再单独维护。 */
  ourRole?: string | null;
  fundraisingStartAt?: string;
  fundraisingEndAt?: string;
  investmentPeriodEndAt?: string;
  exitPeriodEndAt?: string | null;
  managementFeeRate?: number;
  carryRate?: number;
  hurdleRate?: number;
  /** GP 收益（carry/catch-up）受信 LP 组织 UUID。 */
  gpCarryLpOrgUuid?: string | null;
  distributionMode?: string;
  /** 分配瀑布（4 层结构；用本字段替代 distributionMode 单选）。 */
  distributionWaterfall?: {
    enabledLayers: number;
    layerRatios: [number, number, number, number];
  };
  distributionFrequency?: string;

  // ── 基本信息补齐（2026-10-23 契约）─────────────────────────────
  requestReturnRatio?: number | null;
  requestReturnAmount?: number | null;
  returnRequirement?: string | null;
  returnCompletion?: string | null;
  investmentPeriodYears?: number | null;
  exitPeriodYears?: number | null;
  isRollOver?: FundYesNo | string | null;
  isPaybackOver?: FundYesNo | string | null;
  investmentPeriodStartAt?: string | null;
  investmentPeriodExtendEndAt?: string | null;
  durationDesc?: string | null;
  investmentManagementFeeBasis?: FundInvestmentMgmtFeeBasis | string | null;
  exitManagementFeeBasis?: FundExitMgmtFeeBasis | string | null;
  exitManagementFeeRate?: number | null;
  extensionManagementFeeType?: FundExtensionMgmtFeeType | string | null;
  managementFeeDesc?: string | null;
  incomeDistributionDesc?: string | null;
  managerExcessRatio?: number | null;
  lpExcessRatio?: number | null;
  custodyFeeDesc?: string | null;
  executionServiceFee?: string | null;
  businessScope?: string | null;
  custodyAgreementRequired?: FundYesNo | string | null;
  delegatedRepresentative?: string | null;
  /** 所属公司组织节点 id（组织树根节点）。 */
  companyId?: string | null;
  /** 所属公司（我方内部组织，非基金管理人）。 */
  companyName?: string | null;
  /** 所属部门组织节点 id（公司下级）。 */
  deptId?: string | null;
  /** 所属部门。 */
  departmentName?: string | null;
  /** 所属小组组织节点 id（部门下级，可空）。 */
  orgGroupId?: string | null;
  /** 所属小组。 */
  groupName?: string | null;
  distributionBasis?: string;
  /** 封闭期（月）。 */
  lockupMonths?: number;
  /** 锁定期（月）。 */
  holdingLockupMonths?: number;
  /** 合规审批备注（留痕）。 */
  complianceNote?: string;
  strategyType?: string | null;
  investmentStages?: string[];
  investmentRegions?: string[];
  investmentRegionUuids?: string[] | null;
  minInvestmentAmount?: number;
  maxInvestmentAmount?: number;
  targetHoldingRatio?: number;
  maxPortfolioCount?: number;
  allowCoInvestment?: boolean;
  allowRelatedParty?: boolean;
  investmentRestrictions?: string;
  manager?: string | null;
  /** 基金经理 IAM 成员身份。 */
  managerMemberUuid?: string | null;
  /** 风控经理显示名。 */
  riskManager?: string | null;
  /** 风控经理 IAM 成员身份。 */
  riskManagerMemberUuid?: string | null;
  ownerName?: string | null;
  /** 负责人成员 UUID：负责人身份以本字段为准。 */
  ownerUuid?: string | null;
  /** 目标规模（万元）。 */
  targetAmount?: number | null;
  /** 最低认缴金额（万元）。 */
  minimumAmount?: number;
  /** 首关目标规模（万元）。 */
  firstCloseTarget?: number | null;
  /** 基金期限（年）。 */
  durationYears?: number | null;
  region?: string | null;
  regionUuid?: string | null;
  strategy?: string | null;
  industries?: string[];
  establishedAt?: string | null;
  firstCloseAt?: string;
  finalCloseAt?: string;
  controlType?: InvestmentFundControlType;
  ourShareRatio?: number | null;
  investmentDecisionDate?: string | null;
  investmentDecisionNo?: string | null;
  decisionConclusion?: string | null;
  lpaSignDate?: string | null;
  gpContactName?: string | null;
  advisorySeat?: string | null;
  keyPersonClause?: string | null;
  managementMode?: FundManagementMode | null;
  managementBasis?: FundManagementBasis | null;
  classificationStatus?: FundClassificationStatus;
  internalManagerOrgUuid?: string | null;
  managementClassificationNote?: string | null;
  totalCommitmentAmount?: number | string | null;
  totalCommitmentSource?: string | null;
  totalCommitmentAsOf?: string | null;
  totalCommitmentAttachmentUuid?: string | null;
  ourInvestorOrgUuids?: string[];
  isAbsoluteControl?: boolean;
  controlRemark?: string | null;
  /** 出资主体UUID（必填；platform 或 fund）。 */
  investmentSubjectUuid?: string | null;
  /** 出资主体类型：platform / fund；与 UUID 成对提交。 */
  investmentSubjectKind?: 'platform' | 'fund' | null;
  /** 管理平台 UUID（独立字段，可空）。 */
  managementPlatformUuid?: string | null;
  /** 来源基金 UUID（参股档案可选）。 */
  sourceFundUuid?: string | null;
  /** 基金性质（枚举值，必填）*/
  fundKind?: FundKind | string | null;
  /** 是否适用国资备案；产品备案另走基金备案流程。 */
  stateAssetFilingRequired?: boolean;
  /** 是否简易管理/简易设立。 */
  isSimplifiedManagement?: FundSimplifiedManagement;
  /** 基金注册状态：已注册 / 未注册。 */
  registrationStatus?: FundRegistrationStatus | string | null;
  /** 基金类型：天使/创投/股权/成长/产业/政府引导/母基金/并购。 */
  fundType?: FundType | string | null;
  /** 基金级别：一级 / 二级 / 三级。 */
  fundLevel?: FundLevel | string | null;
  /** 法定代表人或执行事务合伙人。 */
  legalRepresentative?: string | null;
  /** 基金自身统一社会信用代码。 */
  unifiedSocialCreditCode?: string | null;
  /** 工商登记时间（YYYY-MM-DD）。 */
  businessRegistrationDate?: string | null;
  /** 工商到期日期（YYYY-MM-DD）。 */
  businessRegistrationExpiryDate?: string | null;
  /** 组织机构代码。 */
  organizationCode?: string | null;
  /** 基金注册地址。 */
  registeredAddress?: string | null;
}

export interface SeedDemoResult {
  seeded: number;
  funds: InvestmentFundRecord[];
}

/** 基金生命周期推进顺序（用于显示历史阶段顺序）。 */
export const FUND_PHASE_FLOW: InvestmentFundPhase[] = [
  '募集期',
  '投资期',
  '延长期',
  '退出期',
  '清算中',
  '清算完成',
  '已退出',
  '已注销',
];
